import React, { Fragment } from 'react';
import {Divider, Image, List, Row, Space, Typography} from "antd";
import {FeedbackModalContentFC} from "../Generic/FeedbackModal";
import {useQuerySearchParams} from "../../../../config/queryClient";
import {StudentExerciseAttempt} from "../../../../types";
import {SpinContainer} from "../../../SpinContainer";
import { AgentMessageFormat, AgentUtteranceAction, Event, isAgentUtteranceEvent, isResumeUserEvent, isUserMaterialsShareStartEvent, isUserUtteranceEvent, UserResumeAction, UserShareMaterialsAction, UserUtteranceAction } from './types';
import { entryToTextMessage, getCreatedAtDelta, isAgentTextMessage } from './utils';
import HtmlTextRenderer from './HtmlTextRenderer';
import ReferenceBadge from './ReferenceBadge';
import { formatTimePassed } from '../../../../utils';
import parse from 'html-react-parser'
import MarkdownTextRenderer from './MarkdownTextRenderer';

const {Text} = Typography;

const MsgBox: React.FC<{ style?: React.CSSProperties }> = props => {
    return (
        <Text
            style={{
                borderRadius: '15px',
                marginLeft: '0.5rem',
                marginRight: '0.5rem',
                padding:'0.75rem',
                ...props.style
            }}
        >
            {props.children}
        </Text>
    );
};

type TranscriptEventFC<UserActionType, AgentActionType> = React.FC<{event: Event<UserActionType, AgentActionType>}>;

const getHumanReadableFromDate = (date: string) =>
    `${new Date(date).toLocaleDateString()} - ${new Date(
        date,
    ).toLocaleTimeString()}`;

const ResumeEvent: TranscriptEventFC<UserResumeAction, null> = ({event}) => (
    <Divider style={{borderColor: 'gray'}}>
        <Text>
            Conversation resumed
        </Text>
        <br/>
        <Text>
            {getHumanReadableFromDate(event.created_at)}
        </Text>
    </Divider>
);

const UserMessageEvent: TranscriptEventFC<UserUtteranceAction, null> = ({event}) => (
    <Row
        id={`user-messsage-${event.user_action?.id}`}
        justify='end'
    >
        <MsgBox
            style={{
                backgroundColor: 'black',
                color: 'white',
                maxWidth: '80%'
            }}
        >
            {event.user_action.payload.text}
            {event.created_at_delta && (
                <div
                    style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginTop: '8px',
                    }}
                >
                    <ReferenceBadge>
                        {formatTimePassed(event.created_at_delta)}
                    </ReferenceBadge>
                </div>
            )}
        </MsgBox>
    </Row>
);

const UserMaterialShareEvent: TranscriptEventFC<UserShareMaterialsAction, null> = ({ event }) => {
    const user_action = event.user_action as UserShareMaterialsAction;
    const {name, url} = user_action.payload;

    return (
        <Row justify='end'>
            <div
                style={{
                    maxWidth: '80%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'end',
                    alignItems: 'end',
                }}
            >
                <Row
                    justify='space-between'
                    style={{
                        width: '100%',
                        alignItems: 'center',
                    }}
                >
                    <div style={{display: 'inline-block'}}>
                        Shared <i>{name}</i>
                    </div>
                    {event.created_at_delta && (
                        <ReferenceBadge>
                            {formatTimePassed(event.created_at_delta)}
                        </ReferenceBadge>
                    )}
                </Row>
                <Image
                    height={175}
                    width={175}
                    src={url}
                />
            </div>
        </Row>
    );
};

const AgentMessage: React.FC<{ event: Event<null, AgentUtteranceAction>, agentMessageFormat: AgentMessageFormat | undefined }> = ({event, agentMessageFormat}) => {
    const message = entryToTextMessage(event, agentMessageFormat);
    if (!message || !isAgentTextMessage(message)) {
        throw new Error('Invalid message format');
    }

    const { text, citations, footnotes, attachments } = message;

    return (
        <Row
            justify='start'
        >
            <MsgBox style={{backgroundColor: 'var(--grey-200)', maxWidth: '80%'}}>
                {event.agent_action?.payload.chunks ? (
                    <Fragment>
                        {agentMessageFormat === 'html' ? (
                            <HtmlTextRenderer
                                text={text}
                            />
                        ) : agentMessageFormat === 'markdown' ? (
                                <MarkdownTextRenderer text={text.join('')} />
                        ) : null}
                    </Fragment>
                ) : (
                    <Fragment>
                        {event.agent_action!.payload.textual.text}
                    </Fragment>
                )}
                {event.created_at_delta && (
                    <div
                        style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginTop: '8px',
                        }}
                    >
                        <ReferenceBadge>
                            {formatTimePassed(event.created_at_delta)}
                        </ReferenceBadge>
                    </div>
                )}
                {(!!attachments?.length ||
                    !!citations?.length ||
                    !!footnotes?.length) && (
                    <List
                        style={{
                            margin: '5px 0px 0px 0px',
                            paddingInlineStart: '20px',
                            fontSize: '0.875rem',
                            wordBreak: 'break-word',
                        }}
                    >
                        {attachments?.map(attachment => (
                            <List.Item key={attachment.name} style={{ fontWeight: 'bold', padding: '0px' }}>
                                {attachment.name}
                            </List.Item>
                        ))}
                        {citations?.map(citation => (
                            <List.Item key={citation?.id} style={{ padding: '0px' }}>
                                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                                    <Text style={{ fontWeight: 'bold', margin: '0px', width: '25px' }}>
                                        {citation?.id}.
                                    </Text>
                                    <Text
                                        style={{ flex: '1', marginInlineStart: '0px !important' }}
                                    >
                                        {parse(
                                            citation?.title ||
                                                citation?.content,
                                        )}
                                    </Text>
                                </div>
                            </List.Item>
                        ))}
                        {!!footnotes?.length && (
                            <div style={{ height: '10px' }} />
                        )}
                        {footnotes?.map(footnote => (
                            <List.Item key={footnote?.id} style={{ fontSize: '0.75rem', padding: '0px' }}>
                                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                                    <Text style={{ fontWeight: 'bold', margin: '0px', width: '25px' }}>
                                        {footnote?.symbol}
                                    </Text>
                                    <Text style={{ flex: 1, margin: '0px' }}>
                                        {footnote?.content}
                                    </Text>
                                </div>
                            </List.Item>
                        ))}
                    </List>
                )}
            </MsgBox>
        </Row>
    )
};


export const Transcript: FeedbackModalContentFC = ({attemptId}) => {

    const {data, isLoading} = useQuerySearchParams<StudentExerciseAttempt>(`/reports/student-exercise-attempts/${attemptId}/`, [], {
        defaultFilters: {
            fields: 'id,feedback'
        }
    })

    const agentMessageFormat: AgentMessageFormat = data?.feedback.stages[0].stage.interaction_stage.agent_message_format;

    if (isLoading) return <SpinContainer/>;

    return <Space direction={'vertical'} size='middle' style={{width: '100%'}}>
        {
            data!.feedback.stages[0].events.map((ev: Event) => {
                ev.created_at_delta = getCreatedAtDelta(ev, data!.feedback.stages[0].events[0]);
                if (isResumeUserEvent(ev)) {
                    return (
                        <ResumeEvent key={ev.user_action?.id} event={ev}/>
                    );
                }
                if (isUserUtteranceEvent(ev)) {
                    return (
                        <UserMessageEvent key={ev.user_action.id} event={ev}/>
                    );
                }
                if (isUserMaterialsShareStartEvent(ev)) {
                    return (
                        <UserMaterialShareEvent
                            key={ev.user_action.id}
                            event={ev}
                        />
                    );
                }
                if (isAgentUtteranceEvent(ev)) {
                    return (
                        <AgentMessage key={ev.agent_action.id} event={ev} agentMessageFormat={agentMessageFormat} />
                    );
                }
                return null;
            })
        }
    </Space>
}

Transcript.title = 'Transcript';
